import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Target, ArrowRight, Terminal, Database, Cpu, GitBranch, ExternalLink, Activity, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import './About.css';

const milestones = [
  {
    year: '2025.08',
    title: '成立 · 技术驱动起跑',
    desc: '吉林卓迈科技有限公司在长春注册成立，创始团队来自甲方安全运营一线。成立即确立技术自研路线——不卖盒子、不转销集成，从第一天开始构建自研安全平台。',
  },
  {
    year: '2025.10',
    title: 'TitanSIEM 自研平台起步',
    desc: '启动 TitanSIEM 态势感知平台研发，基于 Gin + AntD Pro + Kafka + Elasticsearch + MongoDB 技术栈构建，聚焦实时日志分析与威胁关联，MTTD 目标锁定 < 15 分钟。',
  },
  {
    year: '2025.12',
    title: '运营商情报体系打通',
    desc: '与中国联通、中国电信建立威胁情报合作通道，接入运营商级安全数据通路，为企业提供云专线安全接入能力，消除互联网暴露面风险。',
  },
  {
    year: '2026.01',
    title: '安全产品矩阵成型',
    desc: '完成 TitanSIEM（态势感知）、TitanIAM（身份管理）、TitanCloud（SD-WAN + IPaaS）三大平台布局，申请软件著作权 10 项，覆盖安全运营全链路。',
  },
  {
    year: '2026.03',
    title: '服务能力正式交付',
    desc: '安全运营、渗透测试、应急响应、漏洞扫描、代码审计、资产测绘六大服务体系上线，团队持有 CISP、CISSP 等专业认证，极速响应承诺写入 SLA。',
  },
  {
    year: '2026.04',
    title: '持续深耕 · 快速迭代',
    desc: 'TitanCloud SD-WAN 平台进入规模化部署阶段，支持 WireGuard 隧道、VXLAN 公网 IP 传输、NSFOCUS WAF/IPS 集成，服务能力持续扩展中。',
  },
];

const capabilities = [
  {
    title: 'TitanSIEM 安全运营平台',
    icon: <Terminal size={24} />,
    color: '#1a6eff',
    items: [
      'Kafka + Elasticsearch 实时分析引擎',
      'rsyslog / Filebeat 多源日志采集',
      'MTTD < 15 分钟告警响应',
      '10+ 种关联分析规则开箱即用',
      '相比商业 SIEM 节省 60% 以上成本',
    ],
  },
  {
    title: '威胁情报联动体系',
    icon: <Database size={24} />,
    color: '#6930e8',
    items: [
      '大模型智能情报分析',
      'CVE + CNVD 双库实时同步',
      '自建 IOC 黑名单库（IP/域名/哈希）',
      '新威胁 48 小时内覆盖扫描',
    ],
  },
  {
    title: '全生命周期服务交付',
    icon: <GitBranch size={24} />,
    color: '#0aab6e',
    items: [
      '安全运营 / 渗透测试 / 应急响应 / 漏洞扫描 / 代码审计 / 资产测绘',
      '安全事件 SLA 写入合同保障',
      '7×24 值班告警 + 每月安全报告',
      '覆盖金融、政务、制造、医疗等行业',
    ],
  },
];

const certs = [
  { name: 'CISP', full: '注册信息安全专业人员' },
  { name: 'CISSP', full: '国际注册信息系统安全专家' },
  { name: 'CISAW', full: '国际注册信息系统审计师' },
  { name: 'ISO27001', full: '信息安全管理体系认证' },
];

const copyrights = [
  {
    name: '卓迈安全云系统',
    file: '卓迈安全云--卓迈安全云系统.pdf',
    tag: '核心平台',
    desc: '一站式安全运营管理平台，统一调度所有安全能力',
  },
  {
    name: '态势感知系统',
    file: '卓迈安全云-态势感知系统.pdf',
    tag: 'TitanSIEM',
    desc: '实时日志分析与威胁关联，MTTD < 15 分钟',
  },
  {
    name: '全流量威胁分析探针系统',
    file: '卓迈安全云-全流量威胁分析探针系统.pdf',
    tag: '威胁检测',
    desc: '基于深度包检测的全流量威胁实时分析与告警',
  },
  {
    name: '威胁情报预警系统',
    file: '卓迈安全云-威胁情报预警系统.pdf',
    tag: '威胁情报',
    desc: '对接运营商与商业情报源，48 小时内覆盖新威胁',
  },
  {
    name: '威胁智能研判系统',
    file: '卓迈安全云-威胁智能研判系统.pdf',
    tag: '智能研判',
    desc: 'AI 辅助告警降噪与威胁等级智能评估',
  },
  {
    name: '自动化编排系统',
    file: '卓迈安全云-自动化编排系统.pdf',
    tag: 'SOAR',
    desc: '安全事件自动化响应编排，提升处置效率',
  },
  {
    name: '安全接入系统',
    file: '卓迈安全云-安全接入系统.pdf',
    tag: '零信任',
    desc: '基于运营商专线的安全接入能力，消除暴露面',
  },
  {
    name: '基础云平台',
    file: '卓迈安全云-基础云平台.pdf',
    tag: 'TitanCloud',
    desc: 'SD-WAN + IPaaS 基础设施平台，WireGuard + VXLAN',
  },
  {
    name: '综合管理平台',
    file: '卓迈安全云-综合管理平台.pdf',
    tag: '集中管控',
    desc: '多平台统一管理门户，资产/策略/告警一体化',
  },
  {
    name: '大模型调度系统',
    file: '卓迈安全云-大模型调度系统.pdf',
    tag: 'AI引擎',
    desc: '安全领域大模型推理调度与知识管理平台',
  },
];

export default function About() {
  const [activeCapability, setActiveCapability] = useState(0);

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <div className="about-page">
      {/* ====== HERO ====== */}
      <section className="about-hero">
        <div className="about-hero__scan" />
        <div className="about-hero__grid" />
        <div className="container about-hero__inner">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >

            <motion.h1 className="about-hero__title text-center" variants={fadeUp}>
              不卖"盒子"的<br />
              <span style={{ color: '#1a6eff' }}>安全服务商</span>
            </motion.h1>
            <motion.p className="about-hero__desc text-center mx-auto" variants={fadeUp}>
              我们不是安全产品的集成商，而是自研平台 + 专业安全团队的组合。
              卓迈科技的目标只有一个——用可量化的 SLA 承诺，帮企业花最少的钱，
              建最扎实的安全防线。
            </motion.p>
            
            <motion.div className="about-hero__stats" variants={fadeUp}>
              {[
                { value: '10yr+', label: '核心团队经验' },
                { value: '10项+', label: '软件著作权' },
                { value: '3大', label: '运营商合作' },
                { value: '6大', label: '安全服务体系' },
              ].map((s) => (
                <div key={s.label} className="about-hero__stat">
                  <span className="about-hero__stat-value">{s.value}</span>
                  <span className="about-hero__stat-label">{s.label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ====== MISSION / VISION / VALUES ====== */}
      <section className="section-padding" style={{ borderBottom: '1px solid rgba(15, 23, 42, 0.05)' }}>
        <div className="container">
          <motion.div 
            className="about-mvv-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {[
              {
                icon: <Target size={32} strokeWidth={1.5} />,
                color: '#1a6eff',
                label: '使命 MISSION',
                title: '让安全结果可量化',
                desc: '每一项安全服务都有可以测量的结果——MTTD、漏洞发现率、响应时间。我们的承诺不是"我们很专业"，而是"MTTD < 15 分钟写进合同"。',
              },
              {
                icon: <Shield size={32} strokeWidth={1.5} />,
                color: '#6930e8',
                label: '愿景 VISION',
                title: '成为最信任的安全运营商',
                desc: '聚焦专业服务——精英团队与三大运营商深度联动。我们追求将安全服务做深、做透，让客户出现问题第一个想到卓迈。',
              },
              {
                icon: <Cpu size={32} strokeWidth={1.5} />,
                color: '#0aab6e',
                label: '理念 VALUES',
                title: '技术自研，拒绝纯集成',
                desc: '核心防御平台均为自主研发。我们相信只有掌握底层技术栈，才能真正控制服务质量和成本。这也是我们敢于承诺 SLA 的底气所在。',
              },
            ].map((item, i) => (
              <motion.div key={item.label} className="about-mvv-item" variants={fadeUp} style={{ '--mvv-color': item.color }}>
                <div className="about-mvv-icon" style={{ color: item.color, background: `${item.color}15` }}>
                  {item.icon}
                </div>
                <div className="about-mvv-content">
                  <div className="about-mvv-label" style={{ color: item.color }}>{item.label}</div>
                  <h3 className="about-mvv-title">{item.title}</h3>
                  <p className="about-mvv-desc">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== TIMELINE ====== */}
      <section className="section-padding">
        <div className="container">
          <motion.div 
            className="section-header-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <h2 className="section-title-large">
              快速成长的<br />
              <span style={{ color: '#1a6eff' }}>安全技术团队</span>
            </h2>
            <p className="section-subtitle">
              从成立到三大平台成型，不到一年时间完成的安全能力构建
            </p>
          </motion.div>

          <div className="about-timeline-grand">
            <div className="about-timeline-grand__line" />
            {milestones.map((m, i) => (
              <motion.div 
                key={m.year} 
                className={`timeline-grand-item ${i % 2 === 0 ? 'timeline-grand-left' : 'timeline-grand-right'}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="timeline-grand-marker">
                  <div className="timeline-grand-dot" />
                </div>
                <div className="timeline-grand-content bento-item">
                  <div className="timeline-grand-time">{m.year}</div>
                  <h4 className="bento-title">{m.title}</h4>
                  <p className="bento-desc" style={{ marginBottom: 0, WebkitLineClamp: 'unset', display: 'block' }}>{m.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== CAPABILITIES ====== */}
      <section className="section-padding bg-light">
        <div className="container">
          <motion.div 
            className="section-header-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <h2 className="section-title-large">技术能力详解</h2>
            <p className="section-subtitle">点击查看每个能力域的具体指标与技术栈</p>
          </motion.div>

          <motion.div 
            className="about-capabilities-grand"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <div className="capabilities-grand-tabs">
              {capabilities.map((cap, i) => (
                <button
                  key={cap.title}
                  className={`cap-grand-tab ${activeCapability === i ? 'active' : ''}`}
                  style={{ '--cap-color': cap.color }}
                  onClick={() => setActiveCapability(i)}
                >
                  <div className="cap-grand-tab__icon" style={{ color: activeCapability === i ? '#fff' : cap.color }}>
                    {cap.icon}
                  </div>
                  <span className="cap-grand-tab__title">{cap.title}</span>
                </button>
              ))}
            </div>

            <div className="capabilities-grand-panel">
              <div className="panel-grand-header">
                <div className="bento-icon" style={{ color: capabilities[activeCapability].color, background: `${capabilities[activeCapability].color}15` }}>
                  {capabilities[activeCapability].icon}
                </div>
                <h3 className="bento-title" style={{ marginBottom: 0 }}>{capabilities[activeCapability].title}</h3>
              </div>
              <ul className="panel-grand-list">
                {capabilities[activeCapability].items.map((item, i) => (
                  <li key={i} className="panel-grand-item">
                    <CheckCircle size={18} color={capabilities[activeCapability].color} style={{ flexShrink: 0 }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ====== CERTS & COPYRIGHTS ====== */}
      <section className="section-padding">
        <div className="container">
          <motion.div 
            className="section-header-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <h2 className="section-title-large">资质与知识产权</h2>
            <p className="section-subtitle">涵盖核心底层技术架构与多项专业资质认证</p>
          </motion.div>

          <motion.div 
            className="certs-grand-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {certs.map((c, i) => (
              <motion.div key={c.name} className="cert-grand-card bento-item" variants={fadeUp}>
                <div className="cert-grand-name">{c.name}</div>
                <div className="cert-grand-full">{c.full}</div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="copyrights-grand-grid mt-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            style={{ marginTop: '48px' }}
          >
            {copyrights.map((cr, i) => (
              <motion.a
                key={cr.name}
                href={`${process.env.PUBLIC_URL}/copyrights/${encodeURIComponent(cr.file)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="copyright-grand-card bento-item"
                variants={fadeUp}
              >
                <div className="copyright-grand-top">
                  <span className="bento-feature-tag" style={{ color: '#1a6eff', background: '#1a6eff15' }}>{cr.tag}</span>
                  <ExternalLink size={16} className="copyright-grand-link-icon" />
                </div>
                <h4 className="bento-title" style={{ fontSize: '1.25rem', marginBottom: '8px' }}>{cr.name}</h4>
                <p className="bento-desc" style={{ WebkitLineClamp: 'unset', display: 'block', fontSize: '0.95rem' }}>{cr.desc}</p>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== CTA ====== */}
      <section className="section-statement section-padding bg-light">
        <div className="container">
          <motion.div 
            className="statement-content text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <Activity size={48} color="#1a6eff" style={{ margin: '0 auto 24px' }} />
            <h2 className="statement-title">
              想了解我们的<br className="d-md-none" />
              <span style={{ color: '#1a6eff' }}>真实能力</span>吗？
            </h2>
            <p className="statement-desc">
              预约一次免费的 30 分钟技术交流，我们告诉你当前最紧迫的 3 个安全风险在哪里。
            </p>
            <Link to="/contact" className="btn-solid-dark">
              预约技术交流
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
